export type VialType = "wholeBlood" | "plasma";

export const vialTypeLabels: Record<VialType, string> = {
  wholeBlood: "Whole Blood",
  plasma: "Plasma",
};

// Legacy v0/v1 prefixes (WB/PL) and current v2 prefixes (W2/L2). The backend
// BarcodeId model parses both, so the scanner must accept both too.
export const vialBarcodePrefixes: Record<VialType, readonly string[]> = {
  wholeBlood: ["WB", "W2"],
  plasma: ["PL", "L2"],
};

export function getVialTypeFromBarcode(
  barcodeId: string
): VialType | undefined {
  for (const [type, prefixes] of Object.entries(vialBarcodePrefixes) as [
    VialType,
    readonly string[],
  ][]) {
    if (prefixes.some((prefix) => barcodeId.startsWith(prefix))) {
      return type;
    }
  }
  return undefined;
}

export function assignedVialTypes(
  samples: readonly { barcodeId?: string | null }[] | undefined
): Partial<Record<VialType, string>> {
  const map: Partial<Record<VialType, string>> = {};
  for (const sample of samples ?? []) {
    if (!sample.barcodeId) {
      continue;
    }
    const type = getVialTypeFromBarcode(sample.barcodeId);
    if (type) {
      map[type] = sample.barcodeId;
    }
  }
  return map;
}

export function isBloodWorkComplete(
  samples: readonly { barcodeId?: string | null }[] | undefined
): boolean {
  const assigned = assignedVialTypes(samples);
  return assigned.wholeBlood != null && assigned.plasma != null;
}

export type BloodWorkStatus = "complete" | "partial" | "missing";

export function bloodWorkStatus(
  samples: readonly { barcodeId?: string | null }[] | undefined
): BloodWorkStatus {
  const assigned = assignedVialTypes(samples);
  const count =
    (assigned.wholeBlood != null ? 1 : 0) + (assigned.plasma != null ? 1 : 0);
  if (count === 2) {
    return "complete";
  }
  if (count === 1) {
    return "partial";
  }
  return "missing";
}

export type ScanAction =
  | { kind: "invalidPrefix"; prefix: string }
  | { kind: "assign"; vialType: VialType; barcodeId: string }
  | { kind: "alreadyAssigned"; vialType: VialType; barcodeId: string }
  | { kind: "needsReassignMode"; vialType: VialType }
  | { kind: "wrongVialForReassign"; reassigning: VialType; scanned: VialType }
  | { kind: "reassign"; vialType: VialType; newBarcodeId: string };

export function decideScanAction({
  barcodeId,
  assignedByType,
  reassigningType,
}: {
  barcodeId: string;
  assignedByType: Partial<Record<VialType, string>>;
  reassigningType: VialType | undefined;
}): ScanAction {
  const vialType = getVialTypeFromBarcode(barcodeId);
  if (!vialType) {
    return { kind: "invalidPrefix", prefix: barcodeId.slice(0, 2) };
  }

  const existing = assignedByType[vialType];
  if (existing == null) {
    return { kind: "assign", vialType, barcodeId };
  }
  if (existing === barcodeId) {
    return { kind: "alreadyAssigned", vialType, barcodeId };
  }
  if (reassigningType === undefined) {
    return { kind: "needsReassignMode", vialType };
  }
  if (reassigningType !== vialType) {
    return {
      kind: "wrongVialForReassign",
      reassigning: reassigningType,
      scanned: vialType,
    };
  }
  return { kind: "reassign", vialType, newBarcodeId: barcodeId };
}
